import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Search, User, LogOut, Facebook, Instagram, Linkedin, Youtube } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import BehencharaLogo from './BehencharaLogo';

const SOCIAL_LINKS = {
  facebook: 'https://www.facebook.com/profile.php?id=61591658203960',
  instagram: 'https://www.instagram.com/bc.behenchara?stkn=ZjZhMW8yeHAwNmFr',
  linkedin: 'https://www.linkedin.com/company/behenchara/',
  youtube: 'https://www.youtube.com/channel/UCfQd2DCY5ov7w3gZn7-QLDQ'
};

const NAV_ITEMS = [
  { name: 'Explore', path: '/community' },
  { name: 'Magazine', path: '/articles' },
  { name: 'Stories', path: '/articles' },
  { name: 'Community', path: '/community' },
  { name: 'Research', path: '/articles?category=Research' },
  { name: 'Opportunities', path: '/careers' },
  { name: 'Events', path: '/events' },
  { name: 'About', path: '/community' }
];

const Navigation = () => {
  const { isAuthenticated, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  return (
    <nav className="sticky top-0 z-50 bg-ivory/95 backdrop-blur-sm border-b border-subtle" data-testid="main-navigation">
      {/* Top mini bar with social */}
      <div className="hidden lg:block border-b border-subtle bg-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-9 text-xs">
            <div className="label-editorial">A GLOBAL WOMEN'S PLATFORM · EST. 2026</div>
            <div className="flex items-center space-x-4">
              <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer" className="text-charcoal-muted hover:text-burgundy transition-colors" data-testid="nav-facebook-link" aria-label="Facebook">
                <Facebook className="h-3.5 w-3.5" />
              </a>
              <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" className="text-charcoal-muted hover:text-burgundy transition-colors" data-testid="nav-instagram-link" aria-label="Instagram">
                <Instagram className="h-3.5 w-3.5" />
              </a>
              <a href={SOCIAL_LINKS.linkedin} target="_blank" rel="noopener noreferrer" className="text-charcoal-muted hover:text-burgundy transition-colors" data-testid="nav-linkedin-link" aria-label="LinkedIn">
                <Linkedin className="h-3.5 w-3.5" />
              </a>
              <a href={SOCIAL_LINKS.youtube} target="_blank" rel="noopener noreferrer" className="text-charcoal-muted hover:text-burgundy transition-colors" data-testid="nav-youtube-link" aria-label="YouTube">
                <Youtube className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <BehencharaLogo className="h-14 w-auto" showText={true} />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center space-x-8">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className="nav-link text-sm text-charcoal hover:text-burgundy font-medium tracking-wide"
                data-testid={`nav-link-${item.name.toLowerCase()}`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Right Actions */}
          <div className="hidden lg:flex items-center space-x-5">
            <button className="text-charcoal hover:text-burgundy transition-colors" data-testid="nav-search-button">
              <Search className="h-5 w-5" />
            </button>
            
            {isAuthenticated ? (
              <>
                <Link to="/profile" className="text-charcoal hover:text-burgundy transition-colors" data-testid="profile-link">
                  <User className="h-5 w-5" />
                </Link>
                <button onClick={handleLogout} className="text-charcoal hover:text-burgundy transition-colors" data-testid="logout-button">
                  <LogOut className="h-5 w-5" />
                </button>
              </>
            ) : (
              <Link
                to="/register"
                className="btn-burgundy px-5 py-2.5"
                data-testid="nav-cta-join-button"
              >
                Join the Sisterhood
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden text-charcoal"
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-ivory border-t border-subtle" data-testid="mobile-menu">
          <div className="px-4 py-6 space-y-4">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className="block text-charcoal hover:text-burgundy font-medium"
                data-testid={`mobile-nav-${item.name.toLowerCase()}`}
              >
                {item.name}
              </Link>
            ))}
            
            <div className="flex items-center space-x-4 pt-4 border-t border-subtle">
              <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer" className="text-charcoal hover:text-burgundy">
                <Facebook className="h-5 w-5" />
              </a>
              <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" className="text-charcoal hover:text-burgundy">
                <Instagram className="h-5 w-5" />
              </a>
              <a href={SOCIAL_LINKS.linkedin} target="_blank" rel="noopener noreferrer" className="text-charcoal hover:text-burgundy">
                <Linkedin className="h-5 w-5" />
              </a>
              <a href={SOCIAL_LINKS.youtube} target="_blank" rel="noopener noreferrer" className="text-charcoal hover:text-burgundy">
                <Youtube className="h-5 w-5" />
              </a>
            </div>
            
            {isAuthenticated ? (
              <>
                <Link to="/profile" onClick={() => setMobileMenuOpen(false)} className="block text-charcoal font-medium">Profile</Link>
                <button onClick={handleLogout} className="block w-full text-left text-charcoal font-medium">Logout</button>
              </>
            ) : (
              <Link
                to="/register"
                onClick={() => setMobileMenuOpen(false)}
                className="btn-burgundy block px-6 py-3 text-center"
                data-testid="mobile-nav-cta"
              >
                Join the Sisterhood
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
